/**
 * components/shared/MedicalDecisionTaskModal.js
 *
 * Phase 2 — Modal éditable pour créer une mission EXECUTE_CAT à partir
 * d'une décision médicale (alerte résolue, HDJ, événement clinique…).
 *
 * Arbitrages utilisateur verrouillés :
 * - Texte pré-rempli (build côté front via medicalDecisionTemplates).
 * - Médecin édite si besoin puis confirme → POST /secretary-tasks/from-medical-decision.
 * - "Ne pas créer" disponible (le médecin peut décider que la SEC n'a rien à faire).
 */
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import api from "@/api";
import {
  DECISION_TITLES,
  PRIORITY_META,
  buildProposedTask,
} from "@/lib/medicalDecisionTemplates";

export default function MedicalDecisionTaskModal({
  open,
  onClose,
  onDone,
  decisionSource,
  patientId,
  patientName,
  context = {},
  sourceRefs = {},
}) {
  const [instructions, setInstructions] = useState("");
  const [priority, setPriority] = useState("NORMAL");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && decisionSource) {
      const proposed = buildProposedTask(decisionSource, {
        ...context,
        patientName,
      });
      setInstructions(proposed.instructions);
      setPriority(proposed.priority);
    }
  }, [open, decisionSource, patientName, context]);

  if (!open) return null;

  const title = DECISION_TITLES[decisionSource] || "Nouvelle mission";
  const prioMeta = PRIORITY_META[priority] || PRIORITY_META.NORMAL;

  const submit = async () => {
    if (!instructions || instructions.trim().length < 10) {
      toast.error("Les instructions doivent faire au moins 10 caractères");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post(
        "/secretary-tasks/from-medical-decision",
        {
          patientId,
          instructions: instructions.trim(),
          priority,
          decisionSource,
          sourceRefs,
        }
      );
      toast.success("Mission envoyée au secrétariat");
      onDone && onDone(data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  const skip = () => {
    onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[200] p-4"
      data-testid="medical-decision-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b bg-amber-50">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-xl">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-slate-900">
                  Mission secrétariat
                </h2>
                <p className="text-xs text-slate-600 mt-0.5">
                  {title}
                  {patientName ? ` — ${patientName}` : ""}
                </p>
              </div>
            </div>
            <button
              onClick={skip}
              className="p-1.5 hover:bg-white/60 rounded-lg"
              aria-label="Fermer"
              data-testid="mdt-modal-close"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-900">
              Le texte ci-dessous sera envoyé au secrétariat pour action. Vous
              pouvez l'éditer avant d'envoyer, ou choisir de ne pas créer de
              mission si aucune action externe n'est nécessaire.
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Priorité
            </label>
            <div className="flex gap-2" role="radiogroup">
              {["URGENT", "NORMAL", "LOW"].map((p) => {
                const meta = PRIORITY_META[p];
                const active = priority === p;
                const colorClasses =
                  meta.color === "red"
                    ? active
                      ? "bg-red-600 text-white border-red-600"
                      : "bg-white text-red-700 border-red-200 hover:border-red-400"
                    : meta.color === "amber"
                    ? active
                      ? "bg-amber-600 text-white border-amber-600"
                      : "bg-white text-amber-700 border-amber-200 hover:border-amber-400"
                    : active
                    ? "bg-slate-600 text-white border-slate-600"
                    : "bg-white text-slate-700 border-slate-200 hover:border-slate-400";
                return (
                  <button
                    key={p}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setPriority(p)}
                    className={`flex-1 px-3 py-2 rounded-lg border-2 text-xs font-semibold transition-all ${colorClasses}`}
                    data-testid={`mdt-priority-${p.toLowerCase()}`}
                  >
                    {meta.label}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5">
              {prioMeta.description}
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1.5">
              Instructions pour le secrétariat
            </label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              className="w-full p-3 border border-slate-300 rounded-lg text-sm resize-none h-36 focus:ring-2 focus:ring-amber-200 focus:border-amber-500 outline-none"
              maxLength={2000}
              data-testid="mdt-instructions"
            />
            <div className="text-right text-[11px] text-slate-400 mt-1">
              {instructions.length} / 2000
            </div>
          </div>
        </div>

        <div className="p-5 border-t bg-slate-50 flex gap-2 justify-between">
          <button
            onClick={skip}
            disabled={loading}
            className="px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-200 rounded-lg"
            data-testid="mdt-skip"
          >
            Ne pas créer
          </button>
          <button
            onClick={submit}
            disabled={loading || instructions.trim().length < 10}
            className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
            data-testid="mdt-confirm"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            Envoyer au secrétariat
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
